// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/FiscalPeriod.yaml
// Generated: 2026-05-31T11:28:48.312Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { fiscalPeriodService } from '@/services/finance/FiscalPeriodService';
import { CreateFiscalPeriod, UpdateFiscalPeriod, FiscalPeriod } from '@/types/finance/FiscalPeriod';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for FiscalPeriod
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const fiscalperiodKeys = {
  all: (tenantId?: string | null) => ['fiscalperiods', tenantId] as const,
  lists: (tenantId?: string | null) => [...fiscalperiodKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...fiscalperiodKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...fiscalperiodKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...fiscalperiodKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all FiscalPeriods
 */
export function useFiscalPeriods(options?: Omit<UseQueryOptions<FiscalPeriod[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<FiscalPeriod[], Error>({
    queryKey: fiscalperiodKeys.lists(tenantId),
    queryFn: () => fiscalPeriodService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single FiscalPeriod by ID
 */
export function useFiscalPeriod(
  id: string,
  options?: Omit<UseQueryOptions<FiscalPeriod | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<FiscalPeriod | undefined, Error>({
    queryKey: fiscalperiodKeys.detail(id, tenantId),
    queryFn: () => fiscalPeriodService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new FiscalPeriod
 */
export function useCreateFiscalPeriod() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<FiscalPeriod, Error, CreateFiscalPeriod, { previousFiscalPeriods?: FiscalPeriod[] }>({
    mutationFn: (data: CreateFiscalPeriod) => fiscalPeriodService.create(data),
    onMutate: async (newFiscalPeriod) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });

      // Snapshot previous value
      const previousFiscalPeriods = queryClient.getQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newFiscalPeriod, id: (newFiscalPeriod as { id?: string }).id ?? 'temp-' + Date.now() } as FiscalPeriod
      ]);

      return { previousFiscalPeriods };
    },
    onError: (_err, _newFiscalPeriod, context) => {
      // Rollback on error
      if (context?.previousFiscalPeriods) {
        queryClient.setQueryData(fiscalperiodKeys.lists(tenantId), context.previousFiscalPeriods);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing FiscalPeriod
 */
export function useUpdateFiscalPeriod() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<FiscalPeriod, Error, { id: string; data: UpdateFiscalPeriod }, { previousFiscalPeriod?: FiscalPeriod; previousFiscalPeriods?: FiscalPeriod[] }>({
    mutationFn: ({ id, data }) => fiscalPeriodService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: fiscalperiodKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });

      // Snapshot previous values
      const previousFiscalPeriod = queryClient.getQueryData<FiscalPeriod>(fiscalperiodKeys.detail(id, tenantId));
      const previousFiscalPeriods = queryClient.getQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<FiscalPeriod>(fiscalperiodKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as FiscalPeriod);

      // Optimistically update list
      queryClient.setQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as FiscalPeriod : item))
      );

      return { previousFiscalPeriod, previousFiscalPeriods };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousFiscalPeriod) {
        queryClient.setQueryData(fiscalperiodKeys.detail(id, tenantId), context.previousFiscalPeriod);
      }
      if (context?.previousFiscalPeriods) {
        queryClient.setQueryData(fiscalperiodKeys.lists(tenantId), context.previousFiscalPeriods);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: fiscalperiodKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a FiscalPeriod
 */
export function useDeleteFiscalPeriod() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousFiscalPeriods?: FiscalPeriod[] }>({
    mutationFn: (id: string) => fiscalPeriodService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });

      // Snapshot previous value
      const previousFiscalPeriods = queryClient.getQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<FiscalPeriod[]>(fiscalperiodKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousFiscalPeriods };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousFiscalPeriods) {
        queryClient.setQueryData(fiscalperiodKeys.lists(tenantId), context.previousFiscalPeriods);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: fiscalperiodKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveFiscalPeriod() {
  const createMutation = useCreateFiscalPeriod();
  const updateMutation = useUpdateFiscalPeriod();

  return {
    mutate: (params: { id?: string; data: CreateFiscalPeriod | UpdateFiscalPeriod }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateFiscalPeriod }, options);
      } else {
        return createMutation.mutate(params.data as CreateFiscalPeriod, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
